/** @file ShortcutsDialog: every binding with chord and label, plus the held keys (read-only). */
import { applyOverrides } from '@zilo/pose3d-shortcuts/applyOverrides';
import { chordLabel } from '@zilo/pose3d-shortcuts/chordLabel';
import type { ReactElement } from 'react';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import { useEditorStore } from '../state/useEditorStore';
import { Button } from '../ui-kit/Button';
import { Kbd } from '../ui-kit/Kbd';
import { Modal } from '../ui-kit/Modal';
import { bindingLabel } from './bindingLabel';
import { heldKeys } from './heldKeys';
import { PLATFORM } from './platform';

/** The key tables and overrides the shell was given. */
export type ShortcutsDialogProps = { bindingTables: Binding[][]; overrides: Binding[]; onClose: () => void };

/** A table of chord / command / context, then "Held keys"; "Open Settings" is the place to change a chord. */
export function ShortcutsDialog({ bindingTables, overrides, onClose }: ShortcutsDialogProps): ReactElement {
  const store = useEditorStore();
  const rows = bindingTables.flatMap((t) => applyOverrides(t, overrides, PLATFORM).table);
  const settings = (): void => { onClose(); store.getState().navigate({ screen: 'settings' }); };
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose}
      footer={<><Button onClick={settings}>Open Settings</Button><Button variant="primary" onClick={onClose}>Close</Button></>}>
      <div style={{ maxHeight: '55vh', overflow: 'auto' }}>
        <table className="p3-table" aria-label="Shortcut list">
          <thead><tr><th>Keys</th><th>Command</th><th>Context</th></tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={`${b.context}/${b.id}`} title={b.id}>
                <td><Kbd>{chordLabel(b.keys, PLATFORM)}</Kbd></td><td>{bindingLabel(b.id)}</td><td className="p3-mono p3-muted">{b.context}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h3 style={{ margin: '12px 0 4px' }}>Held keys</h3>
        <table className="p3-table" aria-label="Held keys">
          <tbody>{heldKeys.map(([keys, what]) => <tr key={keys}><td><Kbd>{keys}</Kbd></td><td>{what}</td></tr>)}</tbody>
        </table>
      </div>
    </Modal>
  );
}
